import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, Instagram } from "lucide-react";
import gsap from "gsap";
import { heroContent } from "../mock";
import SocialDock from "./SocialDock";
import SegmentedControl from "./SegmentedControl";

const ensureContainer = () => {
  let node = document.getElementById("menu-portal-root");
  if (!node) {
    node = document.createElement("div");
    node.id = "menu-portal-root";
    document.body.appendChild(node);
  }
  return node;
};

// TikTok Icon Component
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.849-1.294-1.98-1.294-3.338v-.5h-3.239v14.167c0 1.938-1.561 3.5-3.5 3.5s-3.5-1.562-3.5-3.5 1.562-3.5 3.5-3.5c.337 0 .663.049.971.14v-3.284a6.738 6.738 0 0 0-.971-.07c-3.584 0-6.5 2.916-6.5 6.5s2.916 6.5 6.5 6.5 6.5-2.916 6.5-6.5V8.626a9.111 9.111 0 0 0 5.013 1.51v-3.239a6.068 6.068 0 0 1-1.9-.335z"/>
  </svg>
);

const FullscreenMenu = ({ isOpen, onClose, items = [], cta }) => {
  const overlayRef = useRef(null);
  const portalTarget = useMemo(() => (typeof window !== "undefined" ? ensureContainer() : null), []);

  // Lock body scroll while menu open
  useEffect(() => {
    const root = document.documentElement;
    if (isOpen) root.classList.add("menu-open");
    else root.classList.remove("menu-open");
    return () => root.classList.remove("menu-open");
  }, [isOpen]);

  useEffect(() => {
    if (!overlayRef.current) return;
    if (isOpen) {
      gsap.set(overlayRef.current, { pointerEvents: "auto", display: "block" });
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.35, ease: "power2.out" }
      );
      gsap.fromTo(
        ".menu-item",
        { y: 28, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.06, ease: "power3.out", overwrite: true }
      );
    } else {
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.25,
        ease: "power2.inOut",
        onComplete: () => gsap.set(overlayRef.current, { pointerEvents: "none", display: "none" })
      });
    }
  }, [isOpen]);

  // Lighting sweep on hover + micro-scale on focus
  useEffect(() => {
    const root = overlayRef.current;
    if (!root) return;
    const links = Array.from(root.querySelectorAll("a.menu-link"));
    const enter = (link) => {
      const sweep = link.querySelector(".sweep");
      if (!sweep) return;
      gsap.fromTo(
        sweep,
        { x: "-120%", opacity: 0 },
        { x: "120%", opacity: 1, duration: 0.8, ease: "power2.out" }
      );
    };
    const leave = (link) => {
      const sweep = link.querySelector(".sweep");
      if (!sweep) return;
      gsap.to(sweep, { opacity: 0, duration: 0.2, ease: "power1.out" });
    };
    const focusIn = (link) => gsap.to(link, { scale: 1.015, duration: 0.18, ease: "power2.out" });
    const focusOut = (link) => gsap.to(link, { scale: 1, duration: 0.18, ease: "power2.inOut" });

    links.forEach((link) => {
      const onEnter = () => enter(link);
      const onLeave = () => leave(link);
      const onFocus = () => focusIn(link);
      const onBlur = () => focusOut(link);
      link.addEventListener("mouseenter", onEnter);
      link.addEventListener("mouseleave", onLeave);
      link.addEventListener("focus", onFocus);
      link.addEventListener("blur", onBlur);
      // store refs for cleanup
      link.__handlers = { onEnter, onLeave, onFocus, onBlur };
    });

    return () => {
      links.forEach((link) => {
        const h = link.__handlers || {};
        link.removeEventListener("mouseenter", h.onEnter);
        link.removeEventListener("mouseleave", h.onLeave);
        link.removeEventListener("focus", h.onFocus);
        link.removeEventListener("blur", h.onBlur);
        delete link.__handlers;
      });
    };
  }, [isOpen]);

  const overlay = (
    <div
      ref={overlayRef}
      className="fixed inset-0 w-screen h-screen z-[999]"
      style={{ pointerEvents: "none", opacity: 0, display: "none" }}
      aria-hidden={!isOpen}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{
          background: `radial-gradient(65% 65% at 50% 10%, rgba(11,11,12,0.9) 0%, rgba(11,11,12,0.88) 40%, rgba(11,11,12,0.85) 100%)`,
          backdropFilter: "blur(6px)"
        }}
      />

      {/* Menu content */}
      <div className="relative h-full w-full flex flex-col" style={{ paddingTop: "max(24px, env(safe-area-inset-top))", paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
        <button
          className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/95 text-neutral-900 grid place-items-center shadow-md hover:shadow-lg transition"
          aria-label="Menü schließen"
          onClick={onClose}
        >
          <X size={22} />
        </button>

        <nav className="flex-1 w-full grid place-items-center px-6">
          <ul className="w-full max-w-3xl space-y-6">
            {items.map((item) => (
              <li key={item.label} className="menu-item">
                <a
                  href={item.to}
                  onClick={onClose}
                  className="menu-link group relative text-white/95 hover:text-white text-4xl md:text-5xl font-semibold tracking-tight flex items-center justify-between focus:outline-none focus-visible:ring-0 drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                >
                  {/* lighting sweep */}
                  <span
                    className="sweep pointer-events-none absolute inset-y-0 left-0 w-1/3 opacity-0"
                    style={{
                      background:
                        "linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0) 100%)",
                      filter: "blur(2px)"
                    }}
                    aria-hidden
                  />
                  <span>{item.label}</span>
                  <ArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <div className="h-px w-full bg-white/15 mt-3" />
              </li>
            ))}
            {cta ? (
              <li className="menu-item pt-2">
                <a
                  href={cta.to}
                  onClick={onClose}
                  className="inline-flex items-center rounded-full px-6 h-12 text-[15px] font-medium shadow hover:shadow-lg transition bg-neutral-900 text-white"
                >
                  {cta.label}
                </a>
              </li>
            ) : null}
            {/* Social Media Icons */}
            <li className="menu-item pt-8">
              <div className="w-full flex justify-center">
                <div className="flex items-center gap-6">
                  <a
                    href="https://www.instagram.com/tomoppenhauser"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:text-white transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50"
                    aria-label="Instagram folgen"
                  >
                    <Instagram size={24} />
                  </a>
                  <a
                    href="https://www.tiktok.com/@tom.oppenhaeuser"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:text-white transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50"
                    aria-label="TikTok folgen"
                  >
                    <TikTokIcon size={24} />
                  </a>
                </div>
              </div>
              <div className="text-center mt-3">
                <p className="text-white/60 text-sm">Folge mir für mehr Insights</p>
              </div>
            </li>
          </ul>
        </nav>
        {/* Mobile-only social dock inside menu */}
        <div className="block md:hidden absolute left-6 bottom-6">
          <SocialDock />
        </div>
      </div>
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(overlay, portalTarget);
};

export default FullscreenMenu;